/**
 * MiloActionLogPanel — Phase 5D foundation surface.
 *
 * Read-only. Reads `GET /api/milo/actions` and renders either an honest
 * empty state (no rows ever inserted in Phase 5D — write paths arrive in
 * Phase 6+) or, in a future phase, a chronological list of action rows.
 *
 * No "Run", no "Apply", no "Confirm", no "Execute", no automation
 * affordances anywhere on the surface. The Action Log is purely a
 * historical record viewer.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  ListChecks, Lock, RefreshCw, AlertCircle, ShieldCheck,
  Clock, BookOpen,
} from 'lucide-react';
import { adminAuthHeader } from '../../data/api';

const API = process.env.REACT_APP_BACKEND_URL;
// Evaluated PER-REQUEST so the real admin token (set at login) is read, not a stale module-load value.
const ADMIN_AUTH = () => adminAuthHeader();

const GOLD = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK = '#1B1812';
const MUTED = '#5C5C66';
const BORDER = 'rgba(0,0,0,0.10)';
const BORDER_GOLD = 'rgba(198,167,90,0.32)';
const SURFACE = '#FFFFFF';
const SOFT_BG = 'rgba(0,0,0,0.025)';
const RED_DK = '#B23A48';
const GREEN_DK = '#1E7344';

const STATUS_TONE = {
  proposed:               { bg: 'rgba(198,167,90,0.10)', fg: GOLD_DK,  br: BORDER_GOLD,                 label: 'Proposed' },
  awaiting_confirmation:  { bg: 'rgba(198,167,90,0.10)', fg: GOLD_DK,  br: BORDER_GOLD,                 label: 'Awaiting Confirmation' },
  confirmed:              { bg: 'rgba(44,102,184,0.08)', fg: '#2C66B8', br: 'rgba(44,102,184,0.26)',    label: 'Confirmed' },
  executed:               { bg: 'rgba(30,115,68,0.08)',  fg: GREEN_DK,  br: 'rgba(30,115,68,0.26)',     label: 'Executed' },
  rejected:               { bg: 'rgba(0,0,0,0.05)',      fg: MUTED,     br: 'rgba(0,0,0,0.16)',         label: 'Rejected' },
  rolled_back:            { bg: 'rgba(178,58,72,0.06)',  fg: RED_DK,    br: 'rgba(178,58,72,0.22)',     label: 'Rolled Back' },
};

const StatusBadge = ({ children, tone = 'gold' }) => {
  const palette = tone === 'gold'
    ? { bg: 'rgba(198,167,90,0.12)', fg: GOLD_DK, br: BORDER_GOLD }
    : tone === 'green'
    ? { bg: 'rgba(30,115,68,0.10)', fg: GREEN_DK, br: 'rgba(30,115,68,0.28)' }
    : tone === 'red'
    ? { bg: 'rgba(178,58,72,0.08)', fg: RED_DK, br: 'rgba(178,58,72,0.26)' }
    : { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.16)' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
      textTransform: 'uppercase', padding: '3px 9px', borderRadius: 99,
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}`,
      display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {children}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const MiloActionLogPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/milo/actions?limit=100`,
        { headers: ADMIN_AUTH() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data?.actions || [];
  const total = data?.total ?? 0;
  const empty = !loading && !error && rows.length === 0;

  return (
    <div data-testid="milo-subtab-panel-action-log"
      style={{ border: `1px solid ${BORDER}`, borderRadius: 14,
        background: SURFACE, padding: '20px 22px',
        display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
          minWidth: 0 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10,
            background: 'rgba(198,167,90,0.10)',
            border: `1px solid ${BORDER_GOLD}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0 }}>
            <ListChecks size={20} color={GOLD_DK} strokeWidth={1.6} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8,
              flexWrap: 'wrap', marginBottom: 4 }}>
              <h3 style={{ margin: 0,
                fontFamily: 'Cormorant Garamond, Georgia, serif',
                fontSize: 22, fontWeight: 700, color: DARK,
                letterSpacing: '0.2px' }}>
                Milo Action Log
              </h3>
              <StatusBadge tone="gold">Phase 5D · Schema foundation</StatusBadge>
              <StatusBadge tone="muted">Read-only · admin-only</StatusBadge>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55,
              maxWidth: 720 }}>
              Historical record of any action Milo has proposed, confirmed,
              executed, or rolled back. Milo remains
              <strong style={{ color: DARK }}> read-only </strong>
              in this phase — no actions exist yet. The action-execution
              write path arrives in a later phase and will land here
              automatically.
            </p>
          </div>
        </div>
        <button data-testid="action-log-refresh-btn" onClick={load}
          disabled={loading}
          title="Refresh"
          style={{ padding: '8px 14px', borderRadius: 9,
            border: `1px solid ${BORDER}`, background: '#FFFFFF',
            color: MUTED, fontWeight: 700, fontSize: 11,
            letterSpacing: '0.5px', textTransform: 'uppercase',
            cursor: loading ? 'wait' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={12}
            style={{ animation: loading ? 'milo-spin 0.9s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Loading / error / empty / populated */}
      {loading && (
        <div data-testid="action-log-loading"
          style={{ padding: '40px 16px', textAlign: 'center',
            color: MUTED, fontSize: 12, fontStyle: 'italic' }}>
          Loading action log…
        </div>
      )}

      {error && !loading && (
        <div data-testid="action-log-error"
          style={{ padding: '10px 12px', borderRadius: 8,
            background: 'rgba(178,58,72,0.06)',
            border: '1px solid rgba(178,58,72,0.22)',
            fontSize: 12, color: RED_DK,
            display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <AlertCircle size={13} /> Couldn't load action log — {error}
        </div>
      )}

      {empty && (
        <div data-testid="action-log-empty"
          style={{ display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 14,
            padding: '52px 24px',
            border: `1px dashed ${BORDER_GOLD}`, borderRadius: 12,
            background: SOFT_BG, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 14,
            background: 'rgba(198,167,90,0.10)',
            border: `1px solid ${BORDER_GOLD}`,
            display: 'flex', alignItems: 'center',
            justifyContent: 'center' }}>
            <ShieldCheck size={26} color={GOLD_DK} strokeWidth={1.5} />
          </div>
          <div style={{ maxWidth: 540 }}>
            <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 20, fontWeight: 700, color: DARK,
              marginBottom: 6 }}>
              No actions yet — Milo is still read-only
            </div>
            <p style={{ margin: 0, fontSize: 13, color: MUTED,
              lineHeight: 1.65 }}>
              No actions have been proposed or executed. Milo can answer
              admin questions in the
              <strong style={{ color: DARK }}> Console </strong>
              tab grounded in the
              <strong style={{ color: DARK }}> Runbooks </strong>
              corpus, but cannot trigger workflows, send messages, or
              mutate data. Future phases may log proposals, confirmations,
              executions, and rollbacks here. Until then, this surface is
              intentionally empty.
            </p>
          </div>
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div data-testid="action-log-rows"
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: MUTED, marginBottom: 2 }}>
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()} action{total === 1 ? '' : 's'}.
          </div>
          {rows.map((r, i) => {
            const tone = STATUS_TONE[r.status] || STATUS_TONE.proposed;
            return (
              <div key={r.id || i}
                data-testid={`action-log-row-${i}`}
                style={{ border: `1px solid ${BORDER}`, borderRadius: 11,
                  background: '#FFFFFF', padding: '14px 16px',
                  display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center',
                  gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.6px', textTransform: 'uppercase',
                    padding: '3px 9px', borderRadius: 99,
                    background: tone.bg, color: tone.fg,
                    border: `1px solid ${tone.br}` }}>
                    {tone.label}
                  </span>
                  {r.kind && (
                    <span style={{ fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.10em', textTransform: 'uppercase',
                      color: MUTED }}>
                      {r.kind}
                    </span>
                  )}
                  {r.scope && (
                    <span style={{ fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                      padding: '2px 7px', borderRadius: 99,
                      background: SOFT_BG, color: DARK,
                      border: `1px solid ${BORDER}` }}>
                      {r.scope}
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: MUTED,
                    marginLeft: 'auto',
                    display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Clock size={11} /> {fmtDate(r.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: DARK, lineHeight: 1.55 }}>
                  {r.summary || <span style={{ color: MUTED, fontStyle: 'italic' }}>(no summary)</span>}
                </div>
                {r.runbook_family_id && (
                  <div style={{ fontSize: 11, color: MUTED,
                    display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <BookOpen size={11} /> Grounded in runbook v{r.runbook_version || '?'}
                  </div>
                )}
                {r.error && (
                  <div style={{ fontSize: 11, color: RED_DK,
                    background: 'rgba(178,58,72,0.06)',
                    padding: '6px 10px', borderRadius: 7,
                    border: '1px solid rgba(178,58,72,0.22)' }}>
                    {r.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Read-only boundary footer */}
      <div data-testid="action-log-boundary-note"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '11px 14px', borderRadius: 10,
          background: 'rgba(198,167,90,0.05)',
          border: `1px solid ${BORDER_GOLD}` }}>
        <Lock size={12} color={GOLD_DK}
          style={{ marginTop: 1, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: MUTED, lineHeight: 1.55 }}>
          This is a historical record viewer.
          <strong style={{ color: DARK }}> No actions can be triggered
          from here</strong>, and Milo currently has no write paths at
          all. When action execution arrives in a later phase, every
          proposal, confirmation, execution, and rollback will be logged
          to this surface for full auditability.
        </div>
      </div>

      <style>{`
        @keyframes milo-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MiloActionLogPanel;
